import { Calendar, dateFnsLocalizer, Navigate } from "react-big-calendar"
import { format } from "date-fns/format"
import { parse } from "date-fns/parse"
import { startOfWeek } from "date-fns/startOfWeek"
import { getDay } from "date-fns/getDay"
import { subDays } from "date-fns/subDays"
import { compareAsc } from "date-fns/compareAsc"
import { enUS } from "date-fns/locale/en-US"
import { fr } from "date-fns/locale/fr"
import "react-big-calendar/lib/css/react-big-calendar.css"
import { MaybeWrapper } from "components/Wrapper"
import { Button } from "components/Button"
import {
  CalendarCheckRegular,
  ChevronLeft,
  ChevronRight,
} from "components/Icon"
import { Text } from "components/Text"
import { useBreakpoints } from "hooks/useBreakpoints"
import * as AdvertType from "enums/AdvertType"
import cx from "classnames"
import "domains/advert/AdvertsCalendar.css"
import { useTranslator } from "components/Translator"
import partition from "lodash/partition"
import keyBy from "lodash/keyBy"

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: {
    en: enUS,
    fr: fr,
  },
})

export const AdvertsCalendar = ({ adverts: advertsProps, onSelectAdvert }) => {
  const translator = useTranslator()

  const advertsToShow = advertsProps
    .filter(
      (advert) =>
        [AdvertType.BOAT, AdvertType.FLEET].includes(advert.type.enum_value) &&
        advert.departureDate &&
        advert.arrivalDate,
    )
    .map((advert) => ({
      ...advert,
      start: new Date(advert.departureDate),
      end: new Date(advert.arrivalDate),
    }))

  const [stopoverAdverts, masterAdverts] = partition(
    advertsToShow,
    (advert) => advert.masterAdvertId,
  )

  const masterAdvertsById = keyBy(masterAdverts, (advert) => advert.id)

  const adverts = []

  for (const advert of masterAdverts) {
    adverts.push(advert)

    adverts.push(
      ...stopoverAdverts
        .filter((stopover) => stopover.masterAdvertId === advert.id)
        .sort((a, b) => compareAsc(new Date(a.start), new Date(b.start)))
        .map((stopover, index, array) => ({
          ...stopover,
          end:
            index !== array.length - 1
              ? subDays(stopover.end, 1)
              : stopover.end,
          masterAdvert: masterAdvertsById[stopover.masterAdvertId],
        })),
    )
  }

  return (
    <Calendar
      localizer={localizer}
      events={adverts}
      views={["month"]}
      style={{ height: 800 }}
      formats={{
        dateFormat: "d",
        weekdayFormat: "EEEEEE",
      }}
      components={{
        toolbar: Toolbar,
        eventWrapper: EventWrapper,
        event: Event,
      }}
      onSelectEvent={onSelectAdvert}
      culture={translator.locale}
      popup={true}
    />
  )
}

const Toolbar = ({ label, onNavigate }) => {
  const { lg } = useBreakpoints()

  return (
    <div className="bg-white py-2 lg:px-2">
      <MaybeWrapper condition={!lg}>
        <div className="flex space-x-2 items-center">
          <div className="shrink">
            <Button
              variant="text"
              color="secondary"
              iconLeft={<ChevronLeft className="w-3" />}
              onClick={() => onNavigate(Navigate.PREVIOUS)}
            />
          </div>
          <div className="grow text-center">
            <Text variant="headline5" className="capitalize">
              {label}
            </Text>
          </div>
          <div className="shrink">
            <Button
              variant="text"
              color="secondary"
              iconLeft={<ChevronRight className="w-3" />}
              onClick={() => onNavigate(Navigate.NEXT)}
            />
          </div>
        </div>
      </MaybeWrapper>
    </div>
  )
}

const EventWrapper = ({ event, children }) => {
  return (
    <div
      title={event.title}
      className={cx("AdvertsCalendar__event", {
        "AdvertsCalendar__event--full": event.completed,
        "AdvertsCalendar__event--master": event.masterAdvertId === null,
        "AdvertsCalendar__event--stopover": event.masterAdvertId !== null,
        "AdvertsCalendar__event--boat":
          event.type.enum_value === AdvertType.BOAT,
        "AdvertsCalendar__event--fleet":
          event.type.enum_value === AdvertType.FLEET,
      })}
    >
      {children}
    </div>
  )
}

const Event = ({ event, continuesPrior, continuesAfter }) => {
  return (
    <div className="flex items-center">
      {event.completed && !continuesPrior ? (
        <div className="shrink-0 mr-2">
          <CalendarCheckRegular className="w-4" />
        </div>
      ) : null}
      {!event.masterAdvertId ? (
        <Text variant="headline6" className="truncate">
          {event.title}
        </Text>
      ) : null}
      {event.masterAdvertId ? (
        <div className="flex justify-between items-center space-x-6 grow">
          <div
            className={cx("min-w-0 grow flex items-center space-x-2", {
              hidden: continuesPrior,
            })}
          >
            <div className="shrink-0">
              <StopIndicator
                variant={
                  event.departureNormalized ===
                  event.masterAdvert.departureNormalized
                    ? "departure"
                    : "stopover"
                }
              />
            </div>
            <div className="truncate">
              {event.departureNormalized || "Missing departureNormalized"}
            </div>
          </div>
          <div
            className={cx("min-w-0 grow flex items-center space-x-2", {
              hidden: continuesAfter,
            })}
          >
            <div className="truncate grow text-right">
              {event.arrivalNormalized || "Missing arrivalNormalized"}
            </div>
            <div className="shrink-0">
              <StopIndicator
                variant={
                  event.arrivalNormalized ===
                  event.masterAdvert.arrivalNormalized
                    ? "arrival"
                    : "stopover"
                }
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

const StopIndicator = ({ variant }) => {
  return (
    <div
      className={cx("w-4 h-4 rounded-full", {
        "bg-secondary-default": variant === "departure",
        "bg-white border-2 border-primary-dark": variant === "stopover",
        "bg-primary-default": variant === "arrival",
      })}
    />
  )
}
